import { useTable } from 'chameleon/ui-stack/ui/base/table';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import usePerformanceTiers, {
  getPerformanceTierType,
} from 'chameleon/src/hooks/usePerformanceTiers';
import { GraySvg, LightPurpleSvg, PurpleSvg } from './assets/Svg';
import React from 'react';

const svgClassName = 'mt-1 mr-2';
const legendHeaderCellClassName = 'flex w-130 items-center justify-center';
const legendCellClassName =
  'font-normal text-12 h-16.8 bg-gray-100 border-gray-300 border-r';

const buildPerformanceLegendItem = ({ label, icon, color }) => ({
  Header: (
    <div className={legendHeaderCellClassName}>
      <div className={svgClassName}>{icon}</div>
      <p>{label}</p>
    </div>
  ),
  accessor: `assessment-${color}`,
  className: legendCellClassName,
  disableSortBy: true,
});

const buildPerformanceLegend = (performanceTiers) => {
  const colors = getPerformanceTierType(performanceTiers);
  return [
    buildPerformanceLegendItem({
      label: 'No Results',
      icon: <GraySvg />,
    }),
    ...performanceTiers.map(({ bottomLimit, color, topLimit, type }) =>
      buildPerformanceLegendItem({
        label: `${bottomLimit}-${topLimit}%`,
        color,
        icon: (
          <Icon
            className={`${colors[type]}-text border border-solid border-gray-500 rounded-full`}
            id="circle"
            width="16"
            height="16"
          />
        ),
      }),
    ),
  ];
};

const ActivityReportTableLegend = ({ isSppEnabled, subjectId }) => {
  const legendHeaderClassName =
    'font-normal text-12 text-center text-gray-700 h-9 bg-gray-100 border-gray-300 border-r border-b';

  const { performanceTiers } = usePerformanceTiers(subjectId);

  const headers = [
    {
      Header: 'Assessment Results',
      id: 'assignment-result-legend-header',
      className: legendHeaderClassName,
      columns: [
        ...buildPerformanceLegend(performanceTiers),
        {
          Header: (
            <div className={legendHeaderCellClassName}>
              <div className={svgClassName}>
                <Icon name="student_scored" size="18px" />
              </div>
              Student Scored
            </div>
          ),
          accessor: 'assessment-student-scored',
          className: legendCellClassName,
          disableSortBy: true,
        },
        {
          Header: (
            <div className={legendHeaderCellClassName}>
              <div className={svgClassName}>
                <Icon name="teacher_and_student_scored" size="32px" />
              </div>
              <TextWithTooltip
                target="Both Scored"
                ariaLabel="Both Scored"
                tooltip="Includes teacher scores, but FRQs were also scored by student."
              >
                <p>Both Scored</p>
              </TextWithTooltip>
            </div>
          ),
          accessor: 'assessment-both-scored',
          className: legendCellClassName,
          disableSortBy: true,
        },
      ],
    },
    {
      Header: 'Video Viewing Progress',
      id: 'video-result-legend-header',
      className: legendHeaderClassName,
      columns: [
        {
          Header: (
            <div className={legendHeaderCellClassName}>
              <div className={svgClassName}>
                <GraySvg />
              </div>
              Not Started
            </div>
          ),
          accessor: 'video-gray',
          className: legendCellClassName,
          disableSortBy: true,
        },
        {
          Header: (
            <div className={legendHeaderCellClassName}>
              <div className={svgClassName}>
                <LightPurpleSvg />
              </div>
              In Progress
            </div>
          ),
          accessor: 'video-light-purple',
          className: legendCellClassName,
          disableSortBy: true,
        },
        {
          Header: (
            <div className={legendHeaderCellClassName}>
              <div className={svgClassName}>
                <PurpleSvg />
              </div>
              Watched
            </div>
          ),
          accessor: 'video-purple',
          className: legendCellClassName,
          disableSortBy: true,
        },
      ],
    },
    ...(isSppEnabled
      ? [
          {
            Header: 'Student Practice Progress',
            id: 'spp-result-legend-header',
            className: legendHeaderClassName,
            columns: [
              {
                Header: (
                  <div className={legendHeaderCellClassName}>
                    <div className={svgClassName}>
                      <GraySvg />
                    </div>
                    Not Started
                  </div>
                ),
                accessor: 'spp-gray',
                className: legendCellClassName,
                disableSortBy: true,
              },
              {
                Header: (
                  <div className={legendHeaderCellClassName}>
                    <div className={svgClassName}>
                      <LightPurpleSvg />
                    </div>
                    Not Submitted
                  </div>
                ),
                accessor: 'spp-light-purple',
                className: legendCellClassName,
                disableSortBy: true,
              },
              {
                Header: (
                  <div className={legendHeaderCellClassName}>
                    <div className={svgClassName}>
                      <PurpleSvg />
                    </div>
                    Submitted
                  </div>
                ),
                accessor: 'spp-purple',
                className: legendCellClassName,
                disableSortBy: true,
              },
            ],
          },
        ]
      : []),
  ];

  const { Table, TableHeader } = useTable({
    data: [],
    headers,
    dependeciesForCalculateHeaders: [
      performanceTiers.map(({ type }) => type).join('.'),
    ],
    sortable: false,
  });

  return (
    <Table className="border w-full my-16">
      <TableHeader showSortIcon={false} />
    </Table>
  );
};

export default ActivityReportTableLegend;
